import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import {
  ArrowLeft,
  User,
  Mail,
  Shield,
  Activity,
  Users,
  GitBranch,
  PlayCircle,
  Brain,
  HardDrive,
  MapPin,
  Monitor,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';

interface UserDetail {
  id: string;
  username: string;
  email: string;
  nickname?: string;
  avatar?: string;
  role: string;
  status: string;
  emailVerified: boolean;
  ip?: string;
  country?: string;
  device?: string;
  lastLoginAt?: string;
  createdAt: string;
  loginCount: number;
  teamCount: number;
  workflowCount: number;
  runCount: number;
  aiUsageCount: number;
  tokenUsed: number;
  storageUsed: number;
  recentLogins: Array<{
    id: string;
    time: string;
    ip: string;
    device: string;
  }>;
  recentRuns: Array<{
    id: string;
    workflowId: string;
    workflowName: string;
    status: string;
    startedAt: string;
    duration: number;
  }>;
  recentAICalls: Array<{
    id: string;
    model: string;
    tokens: number;
    cost: number;
    createdAt: string;
  }>;
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      fetchUserDetail(id);
    }
  }, [id]);

  const fetchUserDetail = async (userId: string) => {
    setIsLoading(true);
    try {
      const response = await userApi.getUserDetail(userId);
      if (response.code === 0) {
        setUser(response.data);
      } else {
        setError('获取用户详情失败');
      }
    } catch (err) {
      console.error('Failed to fetch user detail:', err);
      setError('获取用户详情失败');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">正常</Badge>;
      case 'inactive':
        return <Badge variant="warning">未激活</Badge>;
      case 'locked':
        return <Badge variant="error">已冻结</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-600">
            <Shield className="w-3 h-3" />
            管理员
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600">
            <User className="w-3 h-3" />
            普通用户
          </span>
        );
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('zh-CN');
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getRunStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-success-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-error-500" />;
      default:
        return <Loader2 className="w-4 h-4 text-warning-500 animate-spin" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/users')} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          返回用户列表
        </Button>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-error-600">{error || '用户不存在'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/users')} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          返回
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">用户详情</h1>
          <p className="text-zinc-500">查看用户详细信息和活动记录</p>
        </div>
      </div>

      {/* User Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-accent-100 flex items-center justify-center text-2xl font-bold text-accent-700">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.username}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                user.username.charAt(0).toUpperCase()
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-semibold text-zinc-900">{user.username}</h2>
                {getRoleBadge(user.role)}
                {getStatusBadge(user.status)}
                {user.emailVerified && (
                  <Badge variant="success" className="bg-success-50 text-success-600">
                    已验证
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div className="flex items-center gap-2 text-zinc-600">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">{user.email}</span>
                </div>
                {user.ip && (
                  <div className="flex items-center gap-2 text-zinc-600">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{user.ip} {user.country && `(${user.country})`}</span>
                  </div>
                )}
                {user.device && (
                  <div className="flex items-center gap-2 text-zinc-600">
                    <Monitor className="w-4 h-4" />
                    <span className="text-sm">{user.device}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-zinc-600">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">注册于 {formatDate(user.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">登录次数</p>
                <p className="text-xl font-semibold text-zinc-900">{user.loginCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">所属团队</p>
                <p className="text-xl font-semibold text-zinc-900">{user.teamCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <GitBranch className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">工作流</p>
                <p className="text-xl font-semibold text-zinc-900">{user.workflowCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <PlayCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">运行次数</p>
                <p className="text-xl font-semibold text-zinc-900">{user.runCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI & Storage Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                <Brain className="w-5 h-5 text-pink-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-zinc-500">AI 使用</p>
                <div className="flex items-center gap-4 mt-1">
                  <span className="text-lg font-semibold text-zinc-900">{user.aiUsageCount} 次</span>
                  <span className="text-sm text-zinc-500">{user.tokenUsed.toLocaleString()} tokens</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">存储使用</p>
                <p className="text-lg font-semibold text-zinc-900">{formatBytes(user.storageUsed)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Logins */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4" />
              最近登录
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {user.recentLogins && user.recentLogins.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {user.recentLogins.map((login) => (
                  <div key={login.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{login.ip}</p>
                      <p className="text-xs text-zinc-500">{login.device}</p>
                    </div>
                    <span className="text-xs text-zinc-400">{formatDate(login.time)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-zinc-500">暂无登录记录</div>
            )}
          </CardContent>
        </Card>

        {/* Recent Runs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <PlayCircle className="w-4 h-4" />
              最近运行
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {user.recentRuns && user.recentRuns.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {user.recentRuns.map((run) => (
                  <div key={run.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getRunStatusIcon(run.status)}
                      <div>
                        <p className="text-sm font-medium text-zinc-900">{run.workflowName}</p>
                        <p className="text-xs text-zinc-500">{run.duration}ms</p>
                      </div>
                    </div>
                    <span className="text-xs text-zinc-400">{formatDate(run.startedAt)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-zinc-500">暂无运行记录</div>
            )}
          </CardContent>
        </Card>

        {/* Recent AI Calls */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Brain className="w-4 h-4" />
              最近 AI 调用
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {user.recentAICalls && user.recentAICalls.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {user.recentAICalls.map((call) => (
                  <div key={call.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{call.model}</p>
                      <p className="text-xs text-zinc-500">{call.tokens} tokens · ${call.cost.toFixed(4)}</p>
                    </div>
                    <span className="text-xs text-zinc-400">{formatDate(call.createdAt)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-zinc-500">暂无 AI 调用记录</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
